import { ManagerLink } from "@/components/ManagerLink";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Flame,
  Info,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  RefreshCw,
  Loader2,
  Table,
  BarChart3,
  Gauge,
  ChevronDown,
  ChevronUp,
  ImagePlus,
  Share2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { useState, useRef, lazy, Suspense } from "react";
import { FormDisplay } from "./FormDisplay";
import { ScrollX } from "@/components/ScrollX";
const HotSeatShareDialog = lazy(() => import("./HotSeatShareDialog"));
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { PressureGaugeCard } from "./PressureGaugeCard";
import { useAuth } from "@/contexts/AuthContext";
import { getCurrentSeasonDates, type SeasonType } from "@/lib/seasonUtils";
import { getHigherLeague, getPromotionTarget } from "@/lib/leagueHierarchy";


interface HotSeatEntry {
  managerId: string;
  managerName: string;
  clubId: string;
  clubName: string;
  assignmentId: string;
  hotSeatScore: number;
  ppg: number;
  played: number;
  position: number | null;
  lastSeasonPosition: number | null;
  expectedPosition: number | null;
  positionVsExpectation: "exceeding" | "meeting" | "below" | "danger";
  daysInRole: number;
  form: ("W" | "D" | "L")[];
  isVacant?: boolean;
}

interface HotSeatTableProps {
  onSelectManager?: (managerId: string) => void;
  leagueCode: string;
  seasonType?: SeasonType;
}

import { getMpiTierDecimal } from "@/lib/mpiTiers";
import { AUTH_DISABLED, IMAGE_SHARING_ENABLED } from "@/lib/featureFlags";
import { Link } from "react-router-dom";
import { useClubSlugMap } from "@/hooks/useClubDirectory";

const ClubNameCell = ({ clubId, clubName, className }: { clubId: string; clubName: string; className?: string }) => {
  const { getClubSlug } = useClubSlugMap();
  const slug = getClubSlug(clubId);
  if (!slug) return <span className={className}>{clubName}</span>;
  return (
    <Link to={`/club/${slug}`} className={`hover:text-primary hover:underline transition-colors ${className ?? ""}`}>
      {clubName}
    </Link>
  );
};

// Score is stored as decimal (0-1), shared MPI tier logic
const getStatusInfo = (score: number) => {
  const tier = getMpiTierDecimal(score);
  return {
    label: tier.label,
    emoji: tier.emoji,
    variant: tier.variant,
    color: tier.textClass,
    bgColor: tier.tailwindBg,
  };
};

type ViewMode = "table" | "meters" | "cards";

// Get season boundaries based on league's season type
const getSeasonDatesForLeague = (seasonType: SeasonType = "split-year") => {
  const { seasonLabel, startDate, endDate, isOffSeason } = getCurrentSeasonDates(seasonType);
  return { startDate, endDate, isOffSeason };
};

// Helper functions
const getOrdinalSuffix = (n: number) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const getPositionChange = (entry: HotSeatEntry) => {
  if (entry.lastSeasonPosition === null || entry.position === null) {
    return null;
  }
  return entry.lastSeasonPosition - entry.position;
};

const getExpectationTooltip = (entry: HotSeatEntry) => {
  if (entry.lastSeasonPosition === null) {
    return `Promoted club - current: ${entry.position ? getOrdinalSuffix(entry.position) : "-"}`;
  }
  const diff = entry.lastSeasonPosition - (entry.position || 0);
  const diffText = diff > 0 ? `+${diff}` : diff.toString();
  return `Last season: ${getOrdinalSuffix(entry.lastSeasonPosition)} → Current: ${entry.position ? getOrdinalSuffix(entry.position) : "-"} (${diffText})`;
};

const renderPositionChange = (change: number | null, isPromoted: boolean) => {
  if (isPromoted) {
    return <span className="ml-1 px-1 py-0.5 text-[9px] font-bold bg-accent text-accent-foreground rounded">NEW</span>;
  }

  if (change === null) return null;

  if (change === 0) {
    return (
      <span className="flex items-center text-xs text-muted-foreground ml-1">
        <Minus className="h-3 w-3" />
      </span>
    );
  }

  if (change > 0) {
    return (
      <span className="flex items-center text-xs text-primary ml-1">
        <TrendingUp className="h-3 w-3" />
        <span className="text-[10px]">{change}</span>
      </span>
    );
  } else {
    return (
      <span className="flex items-center text-xs text-destructive ml-1">
        <TrendingDown className="h-3 w-3" />
        <span className="text-[10px]">{Math.abs(change)}</span>
      </span>
    );
  }
};

const renderForm = (form: ("W" | "D" | "L")[]) => {
  return <FormDisplay form={form} variant="letters" size="md" padTo={5} className="justify-center" />;
};

// Presentational component for table view - receives entries as props
interface HotSeatRankingsTableViewProps {
  entries: HotSeatEntry[];
  onSelectManager?: (managerId: string) => void;
}

const HotSeatRankingsTableView = ({ entries, onSelectManager }: HotSeatRankingsTableViewProps) => {
  const navigate = useNavigate();
  if (entries.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">No manager data available</p>
      </div>
    );
  }

  // Sort by hot seat score ascending (safest first); pre-season ties fall back to expected position
  const sortedEntries = [...entries].sort(
    (a, b) => a.hotSeatScore - b.hotSeatScore || (a.expectedPosition ?? 99) - (b.expectedPosition ?? 99),
  );

  return (
    <ScrollX>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border">
            <th className="text-left py-2 px-2 font-semibold text-muted-foreground w-8">#</th>
            <th className="text-left py-2 px-2 font-semibold text-muted-foreground">Manager</th>
            <th className="text-left py-2 px-2 font-semibold text-muted-foreground hidden sm:table-cell">Club</th>
            <th className="text-center py-2 px-2 font-semibold text-muted-foreground hidden md:table-cell">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center justify-center gap-1 cursor-help">
                      <span>Pos vs Exp</span>
                      <Info className="h-3 w-3 text-muted-foreground" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[280px]">
                    <p className="text-xs font-normal">
                      <strong>Current Position vs Expected Position</strong>
                      <br />
                      <br />
                      Expected position is calculated using:
                      <br />• Weighted 3-season average (recent seasons count more)
                      <br />• Promoted clubs target survival
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </th>
            <th className="text-center py-2 px-2 font-semibold text-muted-foreground hidden lg:table-cell">Form</th>
            <th className="text-center py-2 px-2 font-semibold text-muted-foreground">PPG</th>
            <th className="text-center py-2 px-2 font-semibold text-muted-foreground hidden lg:table-cell">Days</th>
            <th className="text-center py-2 px-2 font-semibold text-muted-foreground">
              <div className="flex items-center justify-center gap-1">
                <Flame className="h-3 w-3" />
                <span>Score</span>
              </div>
            </th>
            <th className="text-center py-2 px-2 font-semibold text-muted-foreground hidden sm:table-cell">Status</th>
            <th className="py-2 px-2 w-8 hidden lg:table-cell"></th>
          </tr>
        </thead>
        <tbody>
          {sortedEntries.map((entry, index) => {
            const status = entry.isVacant ? null : getStatusInfo(entry.hotSeatScore);
            const rank = index + 1;

            return (
              <tr
                key={entry.assignmentId || entry.clubId}
                className={`border-b border-border/50 ${
                  entry.isVacant
                    ? "bg-muted/30"
                    : entry.hotSeatScore >= 0.8
                      ? "bg-destructive/5"
                      : entry.hotSeatScore >= 0.6
                        ? "bg-orange-500/5"
                        : ""
                }`}
              >
                <td className="py-2 px-2 text-muted-foreground">{entry.isVacant ? "-" : rank}</td>
                <td className="py-2 px-2 font-medium truncate max-w-[120px]">
                  {entry.isVacant ? (
                    <span className="text-muted-foreground italic flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      Vacant
                    </span>
                  ) : (
                    <>
                      <ManagerLink
                        managerId={entry.managerId}
                        onFallbackClick={() => onSelectManager?.(entry.managerId)}
                        className="hover:text-primary hover:underline transition-colors text-left"
                      >
                        {entry.managerName}
                      </ManagerLink>
                      <ClubNameCell clubId={entry.clubId} clubName={entry.clubName} className="block text-xs text-muted-foreground sm:hidden" />
                    </>
                  )}
                </td>
                <td className="py-2 px-2 text-muted-foreground truncate max-w-[100px] hidden sm:table-cell">
                  <ClubNameCell clubId={entry.clubId} clubName={entry.clubName} />
                </td>
                <td className="py-2 px-2 text-center hidden md:table-cell">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex items-center justify-center cursor-help gap-1">
                          <span
                            className={
                              entry.position && entry.expectedPosition
                                ? entry.position < entry.expectedPosition
                                  ? "text-primary font-medium"
                                  : entry.position > entry.expectedPosition
                                    ? "text-destructive font-medium"
                                    : ""
                                : ""
                            }
                          >
                            {entry.position ? getOrdinalSuffix(entry.position) : "-"}
                          </span>
                          <span className="text-muted-foreground">/</span>
                          <span className="text-muted-foreground">
                            {entry.expectedPosition ? getOrdinalSuffix(entry.expectedPosition) : "-"}
                          </span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs">
                          Current: {entry.position ? getOrdinalSuffix(entry.position) : "-"} / Expected:{" "}
                          {entry.expectedPosition ? getOrdinalSuffix(entry.expectedPosition) : "-"}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </td>
                <td className="py-2 px-2 text-center hidden lg:table-cell">{renderForm(entry.form)}</td>
                <td
                  className={`num py-2 px-2 text-center ${
                    entry.ppg >= 2 ? "text-primary" : entry.ppg < 1 ? "text-destructive" : ""
                  }`}
                >
                  {entry.ppg.toFixed(2)}
                </td>
                <td className="num py-2 px-2 text-center text-muted-foreground hidden lg:table-cell">{entry.daysInRole}</td>
                <td
                  className={`num py-2 px-2 text-center font-bold ${entry.isVacant ? "text-muted-foreground" : status?.color}`}
                >
                  {entry.isVacant
                    ? "-"
                    : entry.hotSeatScore * 100 < 5
                      ? (entry.hotSeatScore * 100).toFixed(1)
                      : Math.round(entry.hotSeatScore * 100)}
                </td>
                <td className="py-2 px-2 text-center hidden sm:table-cell">
                  {entry.isVacant ? (
                    <Badge variant="outline" className="text-xs bg-muted">
                      🔍 Searching
                    </Badge>
                  ) : (
                    <Badge variant={status?.variant} className="text-xs">
                      {status?.emoji} {status?.label}
                    </Badge>
                  )}
                </td>
                <td className="py-2 px-1 hidden lg:table-cell">
                  {!AUTH_DISABLED && !entry.isVacant && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      title="Create graphic"
                      aria-label="Create graphic"
                      onClick={() => navigate(`/create?type=hot-seat&manager=${entry.managerId}`)}
                    >
                      <ImagePlus className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </ScrollX>
  );
};

// Simplified table view with hot seat meter bars
const HotSeatMetersView = ({
  onSelectManager,
  entries,
}: {
  onSelectManager?: (managerId: string) => void;
  entries: HotSeatEntry[];
}) => {
  if (entries.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">No manager data available</p>
      </div>
    );
  }

  // Sort by hot seat score ascending (safest first) for meters view
  const sortedEntries = [...entries].sort(
    (a, b) => a.hotSeatScore - b.hotSeatScore || (a.expectedPosition ?? 99) - (b.expectedPosition ?? 99),
  );

  return (
    <ScrollX>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border">
            <th className="text-left py-2 px-2 font-semibold text-muted-foreground">Manager</th>
            <th className="text-left py-2 px-2 font-semibold text-muted-foreground hidden sm:table-cell">Club</th>
            <th className="text-left py-2 px-2 font-semibold text-muted-foreground w-1/2">
              <div className="flex items-center gap-1">
                <Flame className="h-3 w-3" />
                <span>MPI</span>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedEntries.map((entry) => {
            const status = getStatusInfo(entry.hotSeatScore);

            return (
              <tr key={entry.assignmentId} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                <td className="py-3 px-2">
                  <button
                    onClick={() => onSelectManager?.(entry.managerId)}
                    className="hover:text-primary hover:underline transition-colors text-left font-medium"
                  >
                    {entry.managerName}
                  </button>
                  {/* Show club on mobile below name */}
                  <ClubNameCell clubId={entry.clubId} clubName={entry.clubName} className="block text-xs text-muted-foreground sm:hidden" />
                </td>
                <td className="py-3 px-2 text-muted-foreground hidden sm:table-cell"><ClubNameCell clubId={entry.clubId} clubName={entry.clubName} /></td>
                <td className="py-3 px-2">
                  <div className="flex items-center gap-3">
                    {/* Hot Seat Meter Bar */}
                    <div className="relative flex-1 h-4 rounded-full bg-secondary overflow-hidden min-w-[120px]">
                      {/* Full gradient background */}
                      <div
                        className="absolute inset-0 rounded-full"
                        style={{
                          background:
                            "linear-gradient(to right, #22c55e 0%, #eab308 40%, #f97316 60%, #ef4444 80%, #dc2626 100%)",
                        }}
                      />
                      {/* Indicator line at current score position */}
                      <div
                        className="absolute top-0 h-full w-1 bg-foreground shadow-lg transition-all duration-500"
                        style={{
                          left: `calc(${entry.hotSeatScore * 100}% - 2px)`,
                        }}
                      />
                      {/* Threshold markers */}
                      <div className="absolute top-0 left-[60%] h-full w-0.5 bg-background/40" />
                      <div className="absolute top-0 left-[80%] h-full w-0.5 bg-background/40" />
                    </div>
                    {/* Score value */}
                    <span className={`num font-bold text-sm w-12 text-right ${status.color}`}>
                      {entry.hotSeatScore * 100 < 5
                        ? (entry.hotSeatScore * 100).toFixed(1)
                        : Math.round(entry.hotSeatScore * 100)}
                    </span>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </ScrollX>
  );
};

const HotSeatTable = ({ onSelectManager, leagueCode }: HotSeatTableProps) => {
  const queryClient = useQueryClient();
  const { isAdmin } = useAuth();
  const [viewMode, setViewMode] = useState<ViewMode>("table");
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [isOpen, setIsOpen] = useState(true);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  // Single unified query for all view modes
  const {
    data: hotSeatData,
    isLoading,
    error,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: ["hot-seat-table-unified", leagueCode],
    queryFn: async () => {
      // Fetch the league's season_type, team_count and relegation_zone from the database
      const { data: leagueData } = await supabase
        .from("leagues")
        .select("season_type, team_count, relegation_zone")
        .eq("code", leagueCode)
        .single();

      const resolvedSeasonType: SeasonType = (leagueData?.season_type as SeasonType) || "split-year";
      const teamCount: number = leagueData?.team_count || 20;
      const relegationZone: number = leagueData?.relegation_zone ?? 3;
      const survivalTarget = Math.max(1, teamCount - relegationZone);
      const { seasonLabel, startDate, endDate } = getCurrentSeasonDates(resolvedSeasonType);

      // First, fetch current season matches to determine which clubs are actually in the league
      const { data: matches } = await supabase
        .from("matches")
        .select("home_club_id, away_club_id, home_goals, away_goals, match_date")
        .eq("league", leagueCode)
        .gte("match_date", startDate)
        .lte("match_date", endDate)
        .order("match_date", { ascending: false });

      const preSeason = (matches?.length || 0) === 0;

      // Build set of current-season club IDs from matches
      const currentSeasonClubIds = new Set<string>();
      (matches || []).forEach((m) => {
        currentSeasonClubIds.add(m.home_club_id);
        currentSeasonClubIds.add(m.away_club_id);
      });

      // Previous season window (used for roster fallback and expectation history)
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth() + 1;
      let prevStartDate: string;
      let prevEndDate: string;
      let historyStartDate: string;

      if (resolvedSeasonType === "calendar-year") {
        const prevYear = year - 1;
        prevStartDate = `${prevYear}-01-01`;
        prevEndDate = `${prevYear}-12-31`;
        historyStartDate = `${prevYear - 2}-01-01`;
      } else {
        // Split-year: previous season is one year back
        const currentStartYear = month >= 8 ? year : year - 1;
        const prevStartYear = currentStartYear - 1;
        prevStartDate = `${prevStartYear}-08-01`;
        prevEndDate = `${prevStartYear + 1}-07-31`;
        historyStartDate = `${prevStartYear - 2}-08-01`;
      }

      // Official season roster (handles promotion/relegation before matches are played)
      const rosterSeason = seasonLabel.includes("/") ? seasonLabel : seasonLabel.slice(-2);
      const { data: participants } = await supabase
        .from("season_participants")
        .select("club_id")
        .eq("league", leagueCode)
        .eq("season", rosterSeason);

      if ((participants?.length || 0) > 0) {
        // Official roster is authoritative: drop relegated clubs, include promoted ones
        currentSeasonClubIds.clear();
        (participants || []).forEach((p: any) => currentSeasonClubIds.add(p.club_id));
      } else if (currentSeasonClubIds.size < teamCount) {
        // Fallback: top up roster from previous season table order
        const { data: prevSeasonMatches } = await supabase
          .from("matches")
          .select("home_club_id, away_club_id, home_goals, away_goals")
          .eq("league", leagueCode)
          .gte("match_date", prevStartDate)
          .lte("match_date", prevEndDate)
          .limit(500);

        // Compute previous season ranking to add teams in order
        const prevTable: Record<string, { points: number; gd: number; gf: number }> = {};
        (prevSeasonMatches || []).forEach((m) => {
          if (!prevTable[m.home_club_id]) prevTable[m.home_club_id] = { points: 0, gd: 0, gf: 0 };
          if (!prevTable[m.away_club_id]) prevTable[m.away_club_id] = { points: 0, gd: 0, gf: 0 };
          const h = prevTable[m.home_club_id],
            a = prevTable[m.away_club_id];
          h.gf += m.home_goals;
          h.gd += m.home_goals - m.away_goals;
          a.gf += m.away_goals;
          a.gd += m.away_goals - m.home_goals;
          if (m.home_goals > m.away_goals) h.points += 3;
          else if (m.home_goals < m.away_goals) a.points += 3;
          else {
            h.points += 1;
            a.points += 1;
          }
        });

        // Sort by points desc, GD desc, GF desc — add missing clubs until we reach teamCount
        const rankedPrevClubs = Object.entries(prevTable)
          .sort(([, a], [, b]) =>
            b.points !== a.points ? b.points - a.points : b.gd !== a.gd ? b.gd - a.gd : b.gf - a.gf,
          )
          .map(([id]) => id)
          .filter((id) => !currentSeasonClubIds.has(id));

        for (const id of rankedPrevClubs) {
          if (currentSeasonClubIds.size >= teamCount) break;
          currentSeasonClubIds.add(id);
        }
      }

      // Weighted expectation from the last three completed seasons in this league
      const buildExpectedPositions = async (): Promise<Map<string, number>> => {
        const result = new Map<string, number>();
        // Both history reads are independent — fire them together.
        const higherLeague = getHigherLeague(leagueCode);
        const [histRes, higherRes] = await Promise.all([
          supabase
            .from("matches")
            .select("season, home_club_id, away_club_id, home_goals, away_goals")
            .eq("league", leagueCode)
            .gte("match_date", historyStartDate)
            .lte("match_date", prevEndDate)
            .limit(3000),
          higherLeague
            ? supabase
                .from("matches")
                .select("home_club_id, away_club_id, home_goals, away_goals")
                .eq("league", higherLeague)
                .gte("match_date", prevStartDate)
                .lte("match_date", prevEndDate)
                .limit(3000)
            : Promise.resolve({ data: null } as any),
        ]);
        const histMatches = histRes.data;

        // Clubs relegated from the league above last season, ranked by their final position there
        const higherLeaguePositions = new Map<string, number>();
        if (higherLeague) {
          const higherMatches = higherRes.data;
          const higherTable: Record<string, { points: number; gd: number; gf: number }> = {};
          (higherMatches || []).forEach((m: any) => {
            if (!higherTable[m.home_club_id]) higherTable[m.home_club_id] = { points: 0, gd: 0, gf: 0 };
            if (!higherTable[m.away_club_id]) higherTable[m.away_club_id] = { points: 0, gd: 0, gf: 0 };
            const h = higherTable[m.home_club_id],
              a = higherTable[m.away_club_id];
            h.gf += m.home_goals;
            h.gd += m.home_goals - m.away_goals;
            a.gf += m.away_goals;
            a.gd += m.away_goals - m.home_goals;
            if (m.home_goals > m.away_goals) h.points += 3;
            else if (m.home_goals < m.away_goals) a.points += 3;
            else {
              h.points += 1;
              a.points += 1;
            }
          });
          Object.entries(higherTable)
            .sort(([, a], [, b]) =>
              b.points !== a.points ? b.points - a.points : b.gd !== a.gd ? b.gd - a.gd : b.gf - a.gf,
            )
            .forEach(([clubId], idx) => higherLeaguePositions.set(clubId, idx + 1));
        }

        // Relegated clubs present in this season's roster: best top-flight finisher expects 1st, next 2nd, ...
        const relegatedExpectation = new Map<string, number>();
        Array.from(currentSeasonClubIds)
          .filter((id) => higherLeaguePositions.has(id))
          .sort((a, b) => (higherLeaguePositions.get(a)! - higherLeaguePositions.get(b)!))
          .forEach((clubId, idx) => relegatedExpectation.set(clubId, idx + 1));



        const bySeason = new Map<string, Record<string, { points: number; gd: number; gf: number }>>();
        (histMatches || []).forEach((m: any) => {
          if (!bySeason.has(m.season)) bySeason.set(m.season, {});
          const t = bySeason.get(m.season)!;
          if (!t[m.home_club_id]) t[m.home_club_id] = { points: 0, gd: 0, gf: 0 };
          if (!t[m.away_club_id]) t[m.away_club_id] = { points: 0, gd: 0, gf: 0 };
          const h = t[m.home_club_id],
            a = t[m.away_club_id];
          h.gf += m.home_goals;
          h.gd += m.home_goals - m.away_goals;
          a.gf += m.away_goals;
          a.gd += m.away_goals - m.home_goals;
          if (m.home_goals > m.away_goals) h.points += 3;
          else if (m.home_goals < m.away_goals) a.points += 3;
          else {
            h.points += 1;
            a.points += 1;
          }
        });

        // Most recent season first, up to three seasons
        const seasonKeys = Array.from(bySeason.keys()).sort().reverse().slice(0, 3);
        const positionsBySeason = seasonKeys.map((key) => {
          const table = bySeason.get(key)!;
          const ranked = Object.entries(table).sort(([, a], [, b]) =>
            b.points !== a.points ? b.points - a.points : b.gd !== a.gd ? b.gd - a.gd : b.gf - a.gf,
          );
          const map = new Map<string, number>();
          ranked.forEach(([clubId], idx) => map.set(clubId, idx + 1));
          return map;
        });

        const weights = [3, 2, 1];
        const lastSeasonPositions = positionsBySeason[0];
        const midTable = Math.ceil(teamCount / 2);

        currentSeasonClubIds.forEach((clubId) => {
          let weightedSum = 0;
          let weightTotal = 0;
          positionsBySeason.forEach((posMap, idx) => {
            const pos = posMap.get(clubId);
            if (pos) {
              weightedSum += pos * weights[idx];
              weightTotal += weights[idx];
            }
          });

          if (relegatedExpectation.has(clubId)) {
            // Relegated from the league above: expectation ranked by their final top-flight position
            result.set(clubId, relegatedExpectation.get(clubId)!);
          } else if (weightTotal > 0 && lastSeasonPositions?.has(clubId)) {
            // Established club: weighted average of its last (up to three) finishes
            result.set(clubId, Math.round(weightedSum / weightTotal));
          } else if (positionsBySeason.length > 0) {
            // Newly promoted (not in last season's table): survival target
            result.set(clubId, survivalTarget);
          } else {
            result.set(clubId, midTable);
          }

        });

        return result;
      };


      const today = new Date().toISOString().split("T")[0];
      // Kick off expectations now so they resolve while scores/assignments load.
      const expectedPromise = buildExpectedPositions().catch(() => new Map<string, number>());
      const { data: scores, error: scoresError } = await supabase
        .from("manager_hot_seat_scores")
        .select(
        `
          *,
          managers!inner(name),
          clubs!inner(name),
          manager_assignments!inner(start_date, end_date)
        `,
        )
        .eq("league", leagueCode)
        .order("hot_seat_score", { ascending: true });

      if (scoresError) throw scoresError;

      // Resolve the CURRENT manager for every rostered club, using the same rule as
      // the standings: latest active assignment, regardless of the league tag on the row
      // (promoted/relegated clubs often keep their old league code on the assignment).
      const rosterClubIds = Array.from(currentSeasonClubIds);
      const currentAssignmentMap = new Map<string, any>();
      if (rosterClubIds.length > 0) {
        const CHUNK = 50;
        const chunks: string[][] = [];
        for (let i = 0; i < rosterClubIds.length; i += CHUNK) {
          chunks.push(rosterClubIds.slice(i, i + CHUNK));
        }
        const chunkResults = await Promise.all(
          chunks.map((chunk) =>
            supabase
              .from("manager_assignments")
              .select("id, manager_id, club_id, start_date, managers!inner(name), clubs!inner(name)")
              .in("club_id", chunk)
              .lte("start_date", today)
              .or(`end_date.is.null,end_date.gte.${today}`),
          ),
        );
        for (const { data: activeAssignments } of chunkResults) {
          for (const a of activeAssignments || []) {
            const existing = currentAssignmentMap.get(a.club_id);
            if (!existing || a.start_date > existing.start_date) {
              currentAssignmentMap.set(a.club_id, a);
            }
          }
        }
      }

      // A stored score row is only trusted when it belongs to the club's CURRENT assignment
      const clubScoreMap = new Map<string, any>();
      for (const score of scores || []) {
        if (!currentSeasonClubIds.has(score.club_id)) continue;
        const current = currentAssignmentMap.get(score.club_id);
        if (!current || current.id !== score.assignment_id) continue;
        clubScoreMap.set(score.club_id, score);
      }
      const filteredScores = Array.from(clubScoreMap.values());

      // Clubs with a current manager but no usable score row (pre-season or manager just changed)
      const assignedClubs = rosterClubIds
        .filter((id) => currentAssignmentMap.has(id) && !clubScoreMap.has(id))
        .map((id) => currentAssignmentMap.get(id));

      // Truly vacant: no active assignment at all
      let vacantClubs: any[] = [];
      const trulyVacantIds = rosterClubIds.filter((id) => !currentAssignmentMap.has(id));
      if (trulyVacantIds.length > 0) {
        const { data: vacantClubData } = await supabase.from("clubs").select("id, name").in("id", trulyVacantIds);
        vacantClubs = vacantClubData || [];
      }

      // Pre-season expectations only needed when some rostered club has no played matches
      const expectedMap =
        preSeason || assignedClubs.length > 0 || vacantClubs.length > 0
          ? await expectedPromise
          : new Map<string, number>();

      if (filteredScores.length === 0 && vacantClubs.length === 0 && assignedClubs.length === 0) {
        return { entries: [], lastUpdated: null, latestMatchDate: null };
      }


      const clubStats = new Map<string, { points: number; gd: number; gf: number }>();
      (matches || []).forEach((m) => {
        if (!clubStats.has(m.home_club_id)) clubStats.set(m.home_club_id, { points: 0, gd: 0, gf: 0 });
        if (!clubStats.has(m.away_club_id)) clubStats.set(m.away_club_id, { points: 0, gd: 0, gf: 0 });
        const homeStats = clubStats.get(m.home_club_id)!;
        const awayStats = clubStats.get(m.away_club_id)!;
        homeStats.gf += m.home_goals;
        homeStats.gd += m.home_goals - m.away_goals;
        awayStats.gf += m.away_goals;
        awayStats.gd += m.away_goals - m.home_goals;
        if (m.home_goals > m.away_goals) homeStats.points += 3;
        else if (m.home_goals < m.away_goals) awayStats.points += 3;
        else {
          homeStats.points += 1;
          awayStats.points += 1;
        }
      });

      const sortedClubs = Array.from(clubStats.entries()).sort((a, b) =>
        b[1].points !== a[1].points
          ? b[1].points - a[1].points
          : b[1].gd !== a[1].gd
            ? b[1].gd - a[1].gd
            : b[1].gf - a[1].gf,
      );
      const positionMap = new Map<string, number>();
      sortedClubs.forEach(([clubId], index) => positionMap.set(clubId, index + 1));

      // Build form map: matches are sorted descending (most recent first)
      // We push results in reverse order so oldest is first, newest is last
      const clubFormMap = new Map<string, ("W" | "D" | "L")[]>();
      (matches || []).forEach((m) => {
        const homeForm = clubFormMap.get(m.home_club_id) || [];
        if (homeForm.length < 5) {
          if (m.home_goals > m.away_goals) homeForm.unshift("W");
          else if (m.home_goals < m.away_goals) homeForm.unshift("L");
          else homeForm.unshift("D");
          clubFormMap.set(m.home_club_id, homeForm);
        }
        const awayForm = clubFormMap.get(m.away_club_id) || [];
        if (awayForm.length < 5) {
          if (m.away_goals > m.home_goals) awayForm.unshift("W");
          else if (m.away_goals < m.home_goals) awayForm.unshift("L");
          else awayForm.unshift("D");
          clubFormMap.set(m.away_club_id, awayForm);
        }
      });

      // Get the most recent last_calculated_at from filtered scores
      const lastUpdated = filteredScores.reduce((latest: string | null, score: any) => {
        if (!score.last_calculated_at) return latest;
        if (!latest) return score.last_calculated_at;
        return new Date(score.last_calculated_at) > new Date(latest) ? score.last_calculated_at : latest;
      }, null);

      // Get latest match date
      const latestMatchDate = matches && matches.length > 0 ? matches[0].match_date : null;

      // Use filtered scores (only current-season clubs)
      const entries: HotSeatEntry[] = filteredScores.map((score: any) => {
        const played = score.played ?? 0;
        return {
          managerId: score.manager_id,
          managerName: score.managers?.name || "Unknown",
          clubId: score.club_id,
          clubName: score.clubs?.name || "Unknown",
          assignmentId: score.assignment_id,
          hotSeatScore: Number(score.hot_seat_score),
          ppg: Number(score.ppg),
          played,
          // No matches played yet this season → no meaningful current position
          position: played === 0 ? null : positionMap.get(score.club_id) || score.current_position,
          lastSeasonPosition: score.last_season_position,
          // The pressure engine is authoritative; the local club-history map is a
          // fallback for clubs whose row has no stored expectation yet
          expectedPosition: score.expected_position ?? expectedMap.get(score.club_id) ?? null,
          positionVsExpectation: score.position_vs_expectation as "exceeding" | "meeting" | "below" | "danger",
          daysInRole: score.days_in_role,
          form: score.recent_form ? (score.recent_form.split("") as ("W" | "D" | "L")[]) : [],
          isVacant: false,
        };
      });

      // Add clubs with active managers but no score rows (season not started yet)
      assignedClubs.forEach((assignment: any) => {
        entries.push({
          managerId: assignment.manager_id,
          managerName: (assignment.managers as any)?.name || "Unknown",
          clubId: assignment.club_id,
          clubName: (assignment.clubs as any)?.name || "Unknown",
          assignmentId: assignment.id,
          hotSeatScore: 0,
          ppg: 0,
          played: 0,
          position: positionMap.get(assignment.club_id) || null,
          lastSeasonPosition: null,
          expectedPosition: expectedMap.get(assignment.club_id) ?? null,
          positionVsExpectation: "meeting",
          daysInRole: Math.floor((Date.now() - new Date(assignment.start_date).getTime()) / 86400000),
          form: clubFormMap.get(assignment.club_id) || [],
          isVacant: false,
        });
      });

      // Add truly vacant clubs (no manager assigned)
      vacantClubs.forEach((club: any) => {
        entries.push({
          managerId: "",
          managerName: "Vacant",
          clubId: club.id,
          clubName: club.name,
          assignmentId: "",
          hotSeatScore: 0,
          ppg: 0,
          played: 0,
          position: positionMap.get(club.id) || null,
          lastSeasonPosition: null,
          expectedPosition: expectedMap.get(club.id) ?? null,
          positionVsExpectation: "meeting",
          daysInRole: 0,
          form: clubFormMap.get(club.id) || [],
          isVacant: true,
        });
      });


      console.log(
        `[HotSeatTable] Fetched ${filteredScores?.length || 0} scores + ${assignedClubs.length} assigned (no score) + ${vacantClubs.length} vacant clubs for ${leagueCode}, last updated: ${lastUpdated}`,
      );
      return { entries, lastUpdated, latestMatchDate };
    },
    staleTime: 5 * 60 * 1000, // scores only change after matchdays
    gcTime: 30 * 60 * 1000,
  });

  const handleRecalculate = async () => {
    setIsRecalculating(true);
    try {
      console.log(`[HotSeatTable] Triggering recalculation for ${leagueCode}...`);
      // Pass force: true to bypass staleness guard for manual recalculations
      const { error: invokeError } = await supabase.functions.invoke("update-hot-seat-scores-pressure", {
        body: { leagueCode, force: true },
      });
      if (invokeError) {
        console.error("Failed to recalculate hot seat scores:", invokeError);
      } else {
        // Wait longer for all managers to be processed
        await new Promise((r) => setTimeout(r, 4000));

        // Invalidate ALL hot-seat related queries to force fresh fetch
        await queryClient.invalidateQueries({ queryKey: ["hot-seat"] });
        await queryClient.invalidateQueries({ queryKey: ["hot-seat-table-unified"] });
        await queryClient.invalidateQueries({ queryKey: ["pressure-gauge"] });

        console.log(`[HotSeatTable] Cache invalidated, refetching...`);
        await refetch();
      }
    } finally {
      setIsRecalculating(false);
    }
  };

  const entries = hotSeatData?.entries || [];
  const lastUpdated = hotSeatData?.lastUpdated;

  const formatLastUpdated = (dateStr: string | null) => {
    if (!dateStr) return "Never";
    const date = new Date(dateStr);
    return format(date, "MMM d, yyyy 'at' h:mm a");
  };

  const rankingsRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <Card className="p-6" ref={rankingsRef}>
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <CollapsibleTrigger className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              <Flame className="h-5 w-5 text-orange-700 dark:text-orange-400" />
              <h2 className="text-lg font-semibold text-foreground">MPI Rankings</h2>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <span role="img" aria-label="About MPI" className="inline-flex">
                      <Info className="h-4 w-4 text-muted-foreground" />
                    </span>
                  </TooltipTrigger>

                  <TooltipContent className="max-w-[300px]">
                    <p>
                      ManagerTable Pressure Index (MPI) A 0–100 score measuring manager pressure based on results,
                      recent form, and league position relative to a rolling 3-season expectation baseline. Higher
                      scores indicate increasing pressure.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            {isOpen ? (
              <ChevronUp className="h-5 w-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-5 w-5 text-muted-foreground" />
            )}
          </CollapsibleTrigger>

          <CollapsibleContent>
            <div className="flex items-center justify-end mt-4 mb-4 flex-wrap gap-2">
              <div className="flex items-center gap-3">
                {IMAGE_SHARING_ENABLED && (
                  <Button variant="outline" size="sm" className="gap-1.5" aria-label="Share pressure table" onClick={() => setShareDialogOpen(true)}>
                    <Share2 className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Share</span>
                  </Button>
                )}
                {/* Last Updated + Recalculate (admin only) */}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground hidden md:inline">
                    Updated: {formatLastUpdated(lastUpdated)}
                  </span>
                  <span className="text-xs text-muted-foreground md:hidden">
                    {lastUpdated ? format(new Date(lastUpdated), "MMM d, h:mm a") : "Never"}
                  </span>
                  {/* Only show recalculate button to admins */}
                  {isAdmin && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRecalculate}
                      disabled={isRecalculating || isRefetching}
                      className="gap-1.5"
                    >
                      {isRecalculating ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <RefreshCw className="h-3.5 w-3.5" />
                      )}
                      <span className="hidden sm:inline">Recalculate</span>
                    </Button>
                  )}
                </div>

                {/* View Mode Toggle */}
                <ToggleGroup
                  type="single"
                  value={viewMode}
                  onValueChange={(value) => value && setViewMode(value as ViewMode)}
                  className="bg-muted rounded-lg p-1"
                >
                  <ToggleGroupItem
                    value="table"
                    aria-label="Table view"
                    className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:shadow-sm px-3 py-1"
                  >
                    <Table className="h-4 w-4 mr-1" />
                    <span className="text-xs hidden sm:inline">Table</span>
                  </ToggleGroupItem>
                  <ToggleGroupItem
                    value="meters"
                    aria-label="Meters view"
                    className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:shadow-sm px-3 py-1"
                  >
                    <BarChart3 className="h-4 w-4 mr-1" />
                    <span className="text-xs hidden sm:inline">Meters</span>
                  </ToggleGroupItem>
                  <ToggleGroupItem
                    value="cards"
                    aria-label="Cards view"
                    className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:shadow-sm px-3 py-1"
                  >
                    <Gauge className="h-4 w-4 mr-1" />
                    <span className="text-xs hidden sm:inline">Cards</span>
                  </ToggleGroupItem>
                </ToggleGroup>
              </div>
            </div>

            {isLoading || isRefetching || isRecalculating ? (
              <div className="space-y-4">
                {isRecalculating && (
                  <div className="flex items-center justify-center gap-2 text-muted-foreground py-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm">Recalculating MPI scores...</span>
                  </div>
                )}
                <Skeleton className="h-[760px] w-full" />
              </div>
            ) : error ? (
              <div className="text-center py-8 space-y-4">
                <AlertTriangle className="h-8 w-8 text-destructive mx-auto" />
                <p className="text-muted-foreground">Failed to load MPI data</p>
                <Button variant="outline" onClick={() => refetch()} className="gap-2">
                  <RefreshCw className="h-4 w-4" />
                  Retry
                </Button>
              </div>
            ) : entries.length === 0 ? (
              <div className="text-center py-8 space-y-4">
                <p className="text-muted-foreground">No manager data available for this league</p>
                <Button variant="outline" onClick={() => refetch()} className="gap-2">
                  <RefreshCw className="h-4 w-4" />
                  Retry
                </Button>
              </div>
            ) : viewMode === "table" ? (
              <HotSeatRankingsTableView entries={entries} onSelectManager={onSelectManager} />
            ) : viewMode === "meters" ? (
              <HotSeatMetersView onSelectManager={onSelectManager} entries={entries} />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {entries.map((entry) => (
                  <PressureGaugeCard
                    key={entry.assignmentId}
                    assignmentId={entry.assignmentId}
                    managerId={entry.managerId}
                    label={`${entry.managerName} @ ${entry.clubName}`}
                  />
                ))}
              </div>
            )}
          </CollapsibleContent>
        </Collapsible>
      </Card>

      {shareDialogOpen && IMAGE_SHARING_ENABLED && (
      <Suspense fallback={null}>
      <HotSeatShareDialog
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        leagueName={leagueCode}
        entries={entries}
      />
      </Suspense>
      )}
    </>
  );
};

export default HotSeatTable;
